/**
 * Shared LiveKit Room options for both OperatorRoom and FieldRoom.
 *
 * Publish defaults bumped to 1080p so iPhone back cameras (and any
 * modern webcam) actually use the resolution they're capable of —
 * the LiveKit React default is 720p which underutilises modern
 * hardware and ends up looking blurry for inspection. This is the
 * connect-time ASSUMPTION (start high, most connections are fine);
 * it isn't the floor. Backlog 2026-09-18 — with simulcast off (see
 * below) there's no lower simulcast layer for a weak field connection
 * to fall back to on its own, so `FieldCommandsHandler` now watches
 * LiveKit's `ConnectionQualityChanged` for the local participant and
 * calls `cameraCommands.applyQualityTier` to drop the SAME track's
 * capture resolution to 640×360 when the connection is genuinely
 * struggling, then back up to this 1080p default once it recovers —
 * see `connectionQuality.ts`. On-device snapshot capture reads the
 * same tier for its JPEG quality (`FieldCaptureHandler`/`FieldLayout`),
 * so the live feed and the captured photo degrade and recover
 * together instead of a flat cut applied to every connection
 * regardless of how it's actually doing.
 *
 * IMPORTANT — only plain literal values in here. LiveKit React's
 * `useLiveKitRoom` keys its Room-creation useEffect on
 * `JSON.stringify(options, roomOptionsStringifyReplacer)`. If the
 * stringified form changes between renders, the hook **creates a new
 * Room** (disconnects the old → reconnects), and every reconnect
 * drops the active CommandBus — breaking annotations, camera.switch,
 * and every other envelope feature mid-session.
 *
 * The earlier version used `VideoPresets.h1080.resolution` (a getter
 * that returns a fresh `VideoResolution` object on each access) and
 * `[VideoPresets.h540, VideoPresets.h216]` (class instances). Either
 * one could make the stringifier produce different output between
 * renders, triggering exactly that reconnect cycle. Replaced with
 * inline literal objects below so JSON.stringify is deterministic.
 *
 * Simulcast layers are not currently configured — lose the adaptive-
 * layering bandwidth saving but for v1 2-participant the cost is
 * negligible and gaining stable Room identity is worth it.
 *
 * Server-side: see `apps/sessions/services.py` —
 * `RoomCompositeEgressRequest` and `ParticipantEgressRequest` use
 * `EncodingOptionsPreset.H264_1080P_30` to match.
 */
import type { RoomOptions } from "livekit-client";

export const ROOM_OPTIONS: RoomOptions = {
  videoCaptureDefaults: {
    resolution: {
      width: 1920,
      height: 1080,
      frameRate: 30,
      aspectRatio: 1920 / 1080,
    },
  },
  // Disable simulcast. We push iPhone back cameras toward 1080p, but Safari
  // frequently CAPTURES 480×640 — so LiveKit's simulcast layers (sized for
  // 1080p) don't match the real encoding. On a camera switch (restartTrack →
  // renegotiate) that surfaces as "cannot set publishing layers, encodings
  // mismatch" and CLOSES the peer connection ("PC manager is closed"), after
  // which every operator command times out (the ack can't be sent). With a
  // single encoding there are no layers to reconcile, so switches renegotiate
  // cleanly. For a 2-participant inspection call the adaptive-layer bandwidth
  // saving is negligible; a stable connection is not.
  publishDefaults: {
    simulcast: false,
  },
  adaptiveStream: true,
  dynacast: true,
};


/** Stable video-capture options for FieldRoom's `<LiveKitRoom video=…>`.
 *
 * MUST be a module constant (not an inline object in the JSX). LiveKitRoom
 * re-evaluates its connect/publish effect when the `video` prop changes
 * identity; an inline `video={{…}}` is a fresh object every render, so any
 * FieldRoom re-render would re-run it → disconnect/reconnect → the CommandBus
 * drops and camera/torch/capture commands time out. Same lesson as
 * ROOM_OPTIONS above, applied to the `video` prop.
 *
 * `facingMode: environment` defaults to the back camera; the explicit 1080p
 * resolution keeps the object from falling back to Safari's 480×640 default.
 * The adaptive downgrade (backlog 2026-09-18, see the ROOM_OPTIONS comment
 * above) applies on top of this via `applyConstraints` after connect — it
 * doesn't change this initial value.
 */
export const FIELD_VIDEO_DEFAULTS = {
  facingMode: "environment" as const,
  resolution: { width: 1920, height: 1080, frameRate: 30 },
};
